/**
 * The academy's own promotions seam: wire types, thunks, query keys, and the readers that
 * decide the handful of things no component should decide on its own.
 *
 * READ `backend/openedx_thinlms_promotions/SEAMS.md` BEFORE CHANGING ANY PATH HERE. The
 * payment-ops seam records what a mismatch costs: the frontend and the pricing backend were
 * built independently, disagreed on a route name, every price read 404'd, and a 404 resolved to
 * "free". These paths are read off `openedx_thinlms_promotions/urls.py`.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT CONTAIN, AND THE ABSENCES ARE THE DESIGN
 * ----------------------------------------------------------------------------
 *   * NO `validateCode`. There is no validate endpoint and there must not be one: Stripe
 *     validates discount codes, on its own hosted page, in its own field. A thinlms opinion
 *     about whether a code is valid is an opinion only Stripe can hold.
 *   * NO discount-entry field anywhere in this app. Two promo fields would let a learner apply
 *     one thinlms code and one Stripe code and receive a reduction nobody priced, and Stripe
 *     accepts at most one discount per Session — so the second field could only ever produce a
 *     conflict.
 *   * NO discount arithmetic. Every number this file carries is one the server read; the
 *     preview's `illustration` is labelled by the server as an illustration of STRIPE's
 *     arithmetic and is rendered with that label attached.
 *   * NO `update`. `PATCH campaigns/<id>` exists and this app does not call it. Stripe freezes
 *     a Coupon's `max_redemptions`, `expires_at` and `restrictions[minimum_amount]` at creation
 *     and offers no call that changes them at any API version, so the only edits that would
 *     land are the local-only ones — and a form that saves the name while silently discarding
 *     the cap beside it is worse than no form. The screen offers a REISSUE and a new campaign
 *     instead.
 *
 * THE ONE READ THAT WAS ADDED, AND WHY IT IS NOT ANY OF THE ABOVE
 * --------------------------------------------------------------
 * `loadScopeOptions` reads `GET scope-options`: the academy's currently-priced, currently-synced
 * items, which are the only items a Stripe Coupon's `applies_to` can name. It exists because the
 * only way to scope a code to ONE course was to paste a `course-v1:` key, and no admin knows
 * those keys. It is a read of the same catalogue the mint-time guard reads, it carries the
 * server's own `amount_minor` for display, and it computes nothing and judges nothing: it is not
 * a validate call, it says nothing about any code, and no arithmetic is done on what it returns.
 *
 * ITS 503 IS NOT AN EMPTY ACADEMY. `interface_unwired` means this deployment cannot answer the
 * question; an empty list means the academy has priced nothing. Rendering both as an empty
 * picker would tell an operator their catalogue is empty on the strength of a request that never
 * reached a catalogue — so `scopeOptionsState` returns a different answer for each.
 */

import { openedx } from '../../shared/lib/openedx';

const PROMOTIONS_API = '/api/promotions/v1';

/** One key for the whole surface, so any write can invalidate everything it could have moved. */
export const PROMOTIONS_KEY = ['promotions'] as const;
export const campaignsKey = () => [...PROMOTIONS_KEY, 'campaigns'] as const;
export const promotionSettingsKey = () => [...PROMOTIONS_KEY, 'settings'] as const;
export const redemptionsKey = (campaignId: number) =>
  [...PROMOTIONS_KEY, 'redemptions', campaignId] as const;
export const scopeOptionsKey = () => [...PROMOTIONS_KEY, 'scope-options'] as const;

/* ----------------------------------------------------------------- wire types ---- */

export const KIND_STRIPE = 'stripe_promotion';
export const KIND_ACCESS = 'access_code';
export type PromoKind = typeof KIND_STRIPE | typeof KIND_ACCESS;

export const DISCOUNT_PERCENT = 'percent';
export const DISCOUNT_FIXED = 'fixed';

export const APPLIES_COURSES = 'courses';
export const APPLIES_PATHWAYS = 'pathways';
export const APPLIES_SPECIFIC = 'specific_items';

/** One named item, in the words the server has for it. `title` is never derived from `item_key`. */
export interface WireScopeLabel {
  item_kind: string;
  item_key: string;
  title: string;
}

/**
 * One item an `applies_to` may name: priced NOW, synced to Stripe NOW.
 *
 * `amount_minor` is the SYNCED price — the number that equals Stripe's `Price.unit_amount`, base
 * plus the baked-in processing markup — and it is displayed through `formatMoney` and never
 * recomputed. Every other number a discount implies is Stripe's to compute.
 */
export interface WireScopeOption extends WireScopeLabel {
  amount_minor: number;
  currency: string;
  stripe_product_id: string;
}

export interface WireScopeOptions {
  courses: WireScopeOption[];
  pathways: WireScopeOption[];
  counts: { courses: number; pathways: number };
}

export interface WireCampaign {
  id: number;
  kind: string;
  code: string;
  name: string;
  internal_note: string;
  discount_type: string;
  /** A DECIMAL AS A STRING, or null. Never parsed to a float for display. */
  percent_off: string | null;
  /** Integer minor units, or null. */
  amount_off_minor: number | null;
  currency: string;
  applies_to: string;
  applies_to_items: string[];
  /**
   * The titles those keys had AT MINT TIME, or absent on a server that does not send them.
   *
   * Absent is not empty: an older deployment sends no such field, and a screen that read that as
   * "this campaign names nothing" would stop showing a scope it can still show as keys. Titles
   * are the mint-time ones on purpose — a course renamed since is still the course the coupon
   * names, and printing today's title would misdescribe what was created.
   */
  applies_to_labels?: WireScopeLabel[];
  /** How many Products this campaign's scope actually resolved to when it was minted. */
  covered_count: number;
  /**
   * How many priced items the academy has NOW, or null when the server did not say.
   *
   * Absent on the list route — the server passes `covered_now=None` there rather than running a
   * catalogue read per row. Null is "not counted", never zero: printing "3 of 0" would read as
   * a broken academy.
   */
  tenant_priced_count: number | null;
  valid_from: string | null;
  valid_until: string | null;
  starts_later: boolean;
  max_total_redemptions: number | null;
  max_per_learner: number;
  min_spend_minor: number | null;
  assigned_email: string;
  /** Pinned to one learner's Stripe customer — the ONE case a per-learner cap is enforced. */
  pinned: boolean;
  per_learner_enforcement: string;
  is_active: boolean;
  sync_state: string;
  /** Local deactivation landed and Stripe's did not. The code STILL WORKS. */
  stripe_still_live: boolean;
  stripe_coupon_id: string;
  stripe_promotion_code_id: string;
  superseded_codes: string[];
  last_error_code: string;
  last_error_message: string;
  created_by_username: string;
  created_at: string;
}

export interface WireCampaignList {
  results: WireCampaign[];
}

export interface WireStripeReadiness {
  ready: boolean;
  state: string;
  message: string;
}

export interface WirePromotionSettings {
  tenant_slug: string;
  promo_codes_enabled: boolean;
  access_codes_enabled: boolean;
  stripe_ready: WireStripeReadiness;
}

export interface WirePreviewIllustration {
  item_key: string;
  price_minor: number;
  total_after_code_minor: number;
  currency: string;
}

export interface WireCampaignPreview {
  covered_products: string[];
  covered_count: number;
  tenant_priced_count: number;
  scope_does_not_grow: string;
  guard_ok: boolean;
  guard_reason: string;
  guard_message: string;
  illustration: WirePreviewIllustration[];
  /** The server's own label. Rendered verbatim — this is not a quote and must not read as one. */
  illustration_label: string;
  per_learner_enforcement_note: string;
  irreversible_cap_warning: string;
}

export interface WireRedemptionSummary {
  [key: string]: unknown;
}

export interface WireRedemptionRow {
  id: number;
  learner_username: string;
  learner_email: string;
  item_kind: string;
  item_key: string;
  state: string;
  source: string;
  amount_subtotal_minor: number | null;
  amount_discount_minor: number | null;
  amount_total_minor: number | null;
  currency: string;
  over_limit: boolean;
  release_reason: string;
  stripe_session_id: string;
  consumed_at: string | null;
  refunded_at: string | null;
  released_at: string | null;
}

export interface WireRedemptions {
  summary: WireRedemptionSummary;
  remedies_for_over_limit: string[];
  results: WireRedemptionRow[];
}

/* --------------------------------------------------------------------- reads ---- */

export const loadPromotionSettings = () =>
  openedx.get<WirePromotionSettings>(`${PROMOTIONS_API}/settings`);

export const loadCampaigns = () =>
  openedx.get<WireCampaignList>(`${PROMOTIONS_API}/campaigns`);

export const loadRedemptions = (campaignId: number) =>
  openedx.get<WireRedemptions>(`${PROMOTIONS_API}/campaigns/${campaignId}/redemptions`);

/**
 * The items this academy's `applies_to` may name: priced NOW, synced to Stripe NOW.
 *
 * Not a validate call and not a price quote. It carries the server's own `amount_minor` so a
 * picker can show what each item costs today, and every other number a discount implies stays
 * Stripe's to compute.
 */
export const loadScopeOptions = () =>
  openedx.get<WireScopeOptions>(`${PROMOTIONS_API}/scope-options`);

/* --------------------------------------------------------------------- writes ---- */

/**
 * The fields a create may set, and nothing else.
 *
 * Mirrors `campaigns.CREATABLE_FIELDS` — the server refuses an unknown field BY NAME rather
 * than dropping it, so a typo here is a 400 naming the field and not a silent no-op. `code` is
 * optional: blank means the server generates one.
 */
export interface CampaignDraft {
  kind: PromoKind;
  code?: string;
  name: string;
  internal_note?: string;
  discount_type?: string;
  /** A STRING, so no float reaches a Decimal column. */
  percent_off?: string;
  amount_off_minor?: number;
  currency?: string;
  applies_to: string;
  applies_to_items?: string[];
  valid_from?: string;
  valid_until?: string;
  max_total_redemptions?: number;
  max_per_learner?: number;
  min_spend_minor?: number;
  assigned_email?: string;
}

export const previewCampaign = (draft: CampaignDraft) =>
  openedx.post<WireCampaignPreview>(`${PROMOTIONS_API}/campaigns/preview`, draft);

export const createCampaign = (draft: CampaignDraft) =>
  openedx.post<WireCampaign>(`${PROMOTIONS_API}/campaigns`, draft);

/**
 * Two writes — ours and Stripe's — and a half-done one answers 502 rather than 200.
 *
 * The screen must not report success until Stripe has agreed. An admin who believes a
 * compromised code is off while it keeps working is the worst outcome of this operation, which
 * is why `stripe_still_live` exists on the row and is rendered.
 */
export const deactivateCampaign = (campaignId: number) =>
  openedx.post<WireCampaign>(`${PROMOTIONS_API}/campaigns/${campaignId}/deactivate`, {});

/**
 * A NEW Promotion Code against the SAME Coupon. The recovery, and the only one there is.
 *
 * A Stripe Promotion Code that reaches `max_redemptions` or `expires_at` is permanently
 * inactive and cannot be reactivated, so a mistyped cap destroys the code. Keeping the Coupon
 * keeps the campaign's economics, its redemption mirror and its reporting.
 */
export const reissueCode = (campaignId: number) =>
  openedx.post<WireCampaign & { stripe_promotion_code_id: string }>(
    `${PROMOTIONS_API}/campaigns/${campaignId}/reissue-code`,
    {},
  );

/* -------------------------------------------------------------------- readers ---- */

/**
 * Which phases this academy may use — three answers per kind, not two.
 *
 * `unknown` while the settings read is in flight or has failed, and it is NOT "off": a screen
 * that renders "discount codes are switched off for this academy" on the strength of a failed
 * request has told an operator something about their configuration that it did not read.
 */
export type PhaseState = 'on' | 'off' | 'unknown';

export function phaseOf(
  settings: WirePromotionSettings | null | undefined,
  kind: PromoKind,
): PhaseState {
  if (!settings) return 'unknown';
  const on =
    kind === KIND_STRIPE ? settings.promo_codes_enabled : settings.access_codes_enabled;
  return on ? 'on' : 'off';
}

/**
 * What a campaign's live state IS, as one value a row can render — and it is the SERVER's
 * fields combined, never a guess.
 *
 * `stripe_still_live` is the one that must not be folded into "inactive": it means our
 * deactivation landed and Stripe's did not, so the code is off on this screen and STILL WORKING
 * at checkout. That is the state an admin most needs to see and the one a boolean would hide.
 */
export type CampaignState =
  | { kind: 'still_live_in_stripe'; sentence: string }
  | { kind: 'inactive' }
  | { kind: 'scheduled' }
  | { kind: 'mint_failed'; code: string; message: string }
  | { kind: 'drifted' }
  | { kind: 'minting' }
  | { kind: 'live' };

export function campaignState(row: WireCampaign): CampaignState {
  if (row.stripe_still_live) {
    return {
      kind: 'still_live_in_stripe',
      sentence:
        'This code is switched off here and STILL LIVE IN STRIPE — our write landed and Stripe’s did not, so learners can still use it. Switch it off in the Stripe dashboard.',
    };
  }
  if (row.sync_state === 'mint_failed') {
    return {
      kind: 'mint_failed',
      code: row.last_error_code || 'unknown',
      message: row.last_error_message || '',
    };
  }
  if (row.sync_state === 'drifted') return { kind: 'drifted' };
  if (row.sync_state === 'minting') return { kind: 'minting' };
  if (!row.is_active) return { kind: 'inactive' };
  if (row.starts_later) return { kind: 'scheduled' };
  return { kind: 'live' };
}

/**
 * How many redemptions are left, or that nobody capped it — three answers, never a zero.
 *
 * A null `max_total_redemptions` is UNCAPPED. Rendering it as `0` would say the opposite of
 * what it means, and rendering it as blank would look like a failed read.
 */
export type CapState =
  | { kind: 'uncapped' }
  | { kind: 'capped'; limit: number };

export function capOf(row: WireCampaign): CapState {
  return typeof row.max_total_redemptions === 'number'
    ? { kind: 'capped', limit: row.max_total_redemptions }
    : { kind: 'uncapped' };
}

/**
 * What the scope picker may say, and the two answers a single list would collapse.
 *
 * `unwired` is a deployment that CANNOT ANSWER the question — the catalogue seam is not
 * registered and the server said so with a 503. `empty` is an academy that has answered and
 * has nothing priced and synced. Rendering both as an empty picker would tell an operator
 * their catalogue is empty on the strength of a request that never reached a catalogue, which
 * is the eighth defect of that shape this project would have shipped.
 *
 * `failed` is neither: something else went wrong and the honest answer is that we do not know.
 */
export type ScopeOptionsState =
  | { kind: 'loading' }
  | { kind: 'unwired'; failure: PromotionFailure }
  | { kind: 'failed'; failure: PromotionFailure }
  | { kind: 'empty' }
  | { kind: 'ready'; options: WireScopeOptions };

export function scopeOptionsState(query: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: WireScopeOptions | undefined;
}): ScopeOptionsState {
  if (query.isPending) return { kind: 'loading' };
  if (query.isError) {
    const failure = explainPromotionFailure(query.error);
    return failure.code === 'interface_unwired'
      ? { kind: 'unwired', failure }
      : { kind: 'failed', failure };
  }
  const options = query.data;
  if (!options) return { kind: 'failed', failure: explainPromotionFailure(null) };
  if (options.courses.length === 0 && options.pathways.length === 0) return { kind: 'empty' };
  return { kind: 'ready', options };
}

/**
 * Every item in one flat list, courses first, each still carrying its own kind.
 *
 * The kind rides along rather than being inferred from the key's namespace: `applies_to_items`
 * is a list of opaque strings and the screen must not start parsing them to decide what they
 * are. Two different key shapes that both begin `course-v1:` is exactly the kind of assumption
 * that survives until the day it does not.
 */
export function scopeOptionList(options: WireScopeOptions): WireScopeOption[] {
  return [...options.courses, ...options.pathways];
}

/**
 * What an existing code covers, in the words to show an admin — and never a guess.
 *
 * Three cases, deliberately distinct:
 *   `named`  the campaign names specific items, and we have a title for each (or the bare key
 *            where the server had no title — an EMPTY title is "we could not name it", never
 *            "it has no name", so the key is shown rather than a blank).
 *   `whole`  the campaign was scoped to a kind, so it covers the items of that kind that were
 *            priced AT MINT TIME. `covered_count` is how many that turned out to be.
 *   `keys`   an older server that sends no `applies_to_labels` at all. Absent is not empty:
 *            the keys are still shown, and the caller says they are keys rather than titles.
 */
export type CampaignScope =
  | { kind: 'named'; items: { key: string; title: string; itemKind: string }[] }
  | { kind: 'keys'; keys: string[] }
  | { kind: 'whole'; appliesTo: string; count: number };

export function campaignScope(row: WireCampaign): CampaignScope {
  if (row.applies_to === APPLIES_SPECIFIC) {
    const labels = row.applies_to_labels;
    if (labels && labels.length > 0) {
      return {
        kind: 'named',
        items: labels.map((label) => ({
          key: label.item_key,
          title: label.title || label.item_key,
          itemKind: label.item_kind,
        })),
      };
    }
    return { kind: 'keys', keys: row.applies_to_items };
  }
  return { kind: 'whole', appliesTo: row.applies_to, count: row.covered_count };
}

/**
 * One line for the table's scope column: what this code covers, short enough for a row.
 *
 * The row used to read `3 items` over the raw `applies_to` value — a count with no names and a
 * database enum an admin never chose. Names are what makes a list of codes scannable, so the
 * first two are printed and the rest counted.
 */
export function campaignScopeSummary(row: WireCampaign): string {
  const scope = campaignScope(row);
  if (scope.kind === 'whole') {
    const what = scope.appliesTo === APPLIES_PATHWAYS ? 'learning paths' : 'courses';
    return `All ${what} priced at the time (${scope.count})`;
  }
  const names =
    scope.kind === 'named' ? scope.items.map((item) => item.title) : scope.keys;
  if (names.length === 0) return 'Nothing named';
  const shown = names.slice(0, 2).join(', ');
  return names.length > 2 ? `${shown} and ${names.length - 2} more` : shown;
}

/* ----------------------------------------------------------------------- copy ---- */

/**
 * The sentences this screen adds, in one place — the same reason `OPS_COPY` and `PRICE_COPY`
 * exist. Sentences the SERVER sends (`guard_message`, `illustration_label`,
 * `irreversible_cap_warning`, `per_learner_enforcement`, `stripe_ready.message`, every error
 * `detail`) are rendered verbatim and are NOT copied in here: they carry measured numbers and
 * Stripe's own refusals, and a local paraphrase is how a screen ends up confidently wrong.
 */
export const PROMO_COPY = {
  learnersEnterInStripe:
    'Learners type a discount code into Stripe’s own field on the checkout page. There is no code box in this app, and there is nothing here that can tell a learner whether their code is valid — only Stripe can.',
  economicsAreFrozen:
    'Stripe fixes a coupon’s discount, its scope, its redemption limit, its expiry and its minimum spend when the coupon is created, and offers no call that changes any of them. So none of those can be edited afterwards, on this screen or anywhere else.',
  reissueExplained:
    'Reissuing keeps the promotion and its economics and mints a NEW code against them. Use it when a code has been shared too widely, or when it has hit its limit or expiry — Stripe cannot reactivate a code that has, so a new one is the only recovery.',
  capIsIrreversible:
    'A total redemption limit cannot be raised, lowered or removed later, and a code that reaches it is permanently dead. Leave it blank unless you are certain of the number.',
  perLearnerIsAdvisory:
    'Stripe has no per-customer limit for a shared code, so a per-learner limit on one is checked AFTER the fact: an over-use is flagged and the learner keeps what they bought. Assign the code to one email address to have Stripe enforce it.',
  hundredPercentIsAnAccessCode:
    'A 100%-off discount is refused: Stripe would adjudicate a zero-total checkout in a place this app cannot intercept. Give the whole item away with an ACCESS CODE instead — it makes no Stripe call at all and keeps working while Stripe is unreachable.',
  scopeIsFixed:
    'The scope is fixed when the code is created. A course or pathway priced after that is NOT covered by it.',
  noEditsOffered:
    'There is no edit here, and that is deliberate rather than missing.',
  scopeIsProductsOnly:
    'A Stripe coupon can only name things that exist in Stripe as Products, so only courses and learning paths that are priced AND synced can be picked. An item missing from this list is not priced yet, or its price has not reached Stripe.',
  scopeCatalogueUnknown:
    'The academy’s priced items could not be read, so this list is NOT “nothing is priced” — it is “we could not ask”. Paste the keys below if you know them; the server checks the scope again before it creates anything.',
  scopeEmpty:
    'This academy has nothing priced and synced to Stripe, so there is nothing a discount code could name. Price a course or a learning path first.',
} as const;

/* -------------------------------------------------------------------- errors ---- */

export interface PromotionFailure {
  code: string;
  message: string;
  /** Set where the operator's next step is not "try again". */
  nextStep: string | null;
}

const NEXT_STEP: Record<string, string> = {
  promo_codes_disabled:
    'Discount codes are not switched on for this academy. Ask the platform operator to enable them; nothing here can turn them on.',
  access_codes_disabled:
    'Access codes are not switched on for this academy. Ask the platform operator to enable them.',
  interface_unwired:
    'This deployment has not been wired to read the academy’s priced items, so no discount code can be created. Access codes are unaffected.',
  economics_immutable:
    'Create a new campaign instead. The old code is deactivated and its redemption history is kept.',
  replacement_old_code_still_live:
    'The replacement exists and the OLD code is still live in Stripe. Switch the old one off in the Stripe dashboard.',
  stripe_unavailable:
    'The academy’s Stripe account could not be reached, so nothing was created. Access codes still work.',
};

/**
 * The server's own sentence, always — plus a next step for the codes whose next step is not a
 * retry. A refusal from Stripe arrives inside `detail`, already through the redaction filter.
 */
export function explainPromotionFailure(error: unknown): PromotionFailure {
  const body = (error as { body?: { detail?: unknown; code?: unknown } } | null)?.body ?? null;
  const status = (error as { status?: unknown } | null)?.status;
  const code = typeof body?.code === 'string' ? body.code : '';
  const detail = typeof body?.detail === 'string' ? body.detail : '';

  if (!code && !detail) {
    return {
      code: typeof status === 'number' ? `http_${status}` : 'unreachable',
      message:
        typeof status === 'number'
          ? `The academy answered ${status} and said no more than that. Nothing here can be assumed to have happened.`
          : 'The academy could not be reached, so we do not know whether anything happened. Reload before trying again.',
      nextStep: null,
    };
  }
  return {
    code: code || `http_${status}`,
    message: detail || 'The academy refused this and gave no reason.',
    nextStep: NEXT_STEP[code] ?? null,
  };
}
